import React, {useState} from 'react';
import {Rate, Input, Button, Popconfirm, message} from 'antd';
import type {TableColumnsType} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';
import {addKeyData, formatDate} from 'utils';
import type {SearchProps} from 'antd/es/input/Search';
import Api from 'configs';

import {TableCustom} from 'components/element/TableCustom';
import {PaginationCustom} from 'components/element/PaginationCustom';
import {useGetCustomerReviews} from 'hooks/report';

export const CustomerReviewsTab: React.FC = () => {
  const [filters, setFilters] = useState<any>({
    page: 1,
    pageSize: 10,
    search: '',
  });
  const {data: getData, isLoading, refetch} = useGetCustomerReviews(filters);

  const columns: TableColumnsType = [
    {
      title: 'Mã đơn hàng',
      dataIndex: 'orderId',
      key: 'code',
      align: 'center',
      render: (data: any) => {
        return <span className="font-semibold text-blue-600">{data?.code || '—'}</span>;
      },
    },
    {
      title: 'Khách hàng',
      dataIndex: 'targetId',
      key: 'client',
      align: 'center',
      render: (client: any) => {
        return (
          <div className="flex flex-col items-center">
            <span className="font-medium text-gray-800">{client?.name || 'Khách hàng'}</span>
            {client?.numberPhone && (
              <span className="text-xs text-gray-500">{client.numberPhone}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Tài xế (Shipper)',
      dataIndex: 'driverId',
      key: 'driver',
      align: 'center',
      render: (driver: any) => {
        return (
          <div className="flex flex-col items-center">
            <span className="font-medium text-gray-800">{driver?.name || 'Shipper'}</span>
            {driver?.numberPhone && (
              <span className="text-xs text-gray-500">{driver.numberPhone}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Đánh giá sao',
      dataIndex: 'rating',
      key: 'rating',
      align: 'center',
      render: (rating: number) => {
        return <Rate defaultValue={rating} disabled />;
      },
    },
    {
      title: 'Ghi chú / Nhận xét của tài xế',
      dataIndex: 'description',
      key: 'description',
      align: 'center',
      render: (desc: string) => {
        return desc ? (
          <span className="text-sm text-gray-700 bg-amber-50 px-2 py-1 rounded border border-amber-200 inline-block max-w-xs">
            📝 {desc}
          </span>
        ) : (
          <span className="text-xs text-gray-400 italic">Không có ghi chú</span>
        );
      },
    },
    {
      title: 'Ngày đánh giá',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      render: data => {
        return <div>{formatDate(data)}</div>;
      },
    },
    {
      title: 'Hành động',
      key: 'action',
      align: 'center',
      width: 100,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Xóa đánh giá"
          description="Bạn có chắc muốn xóa đánh giá này của tài xế?"
          okText="Xóa"
          cancelText="Hủy"
          okButtonProps={{danger: true}}
          onConfirm={async () => {
            try {
              await Api.delete(`/reviews/${record?._id}`);
              message.success('Đã xóa đánh giá thành công');
              refetch();
            } catch {
              message.error('Lỗi khi xóa đánh giá');
            }
          }}>
          <Button danger size="small" icon={<DeleteOutlined />}>
            Xóa
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const onSearchValue: SearchProps['onSearch'] = (value: string) => {
    setFilters({...filters, search: value, page: 1});
  };

  return (
    <div className="flex-col gap-4 mt-2">
      <div className="flex justify-between items-center mb-4">
        <div>
          <span className="text-gray-500 text-sm">
            Tổng số: <b>{getData?.total || 0}</b> đánh giá từ tài xế
          </span>
        </div>
        <div>
          <Input.Search
            className="w-96"
            placeholder="Tìm theo ghi chú hoặc mã đơn..."
            onSearch={onSearchValue}
            enterButton
            allowClear
          />
        </div>
      </div>
      <div className="flex flex-col items-end gap-3">
        <TableCustom
          isLoading={isLoading}
          columns={columns}
          data={addKeyData(getData?.result)}
        />
        <PaginationCustom
          totalPage={getData?.totalPage ?? 1}
          current={getData?.page ?? 1}
          pageSize={getData?.pageSize ?? 10}
          onChange={(page, pageSize) => {
            setFilters({...filters, page, pageSize});
          }}
          onShowSizeChange={(current, size) => {
            setFilters({...filters, page: current, pageSize: size});
          }}
        />
      </div>
    </div>
  );
};
